import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Divider, Filters, TFilterItem, Input, IconButton, RadioList } from '@treezor/design-system';

import { ICompanyMemberRequest } from '@/features/company-members/store/models/company-members.model';
import { useCompanyMembers } from '@/features/company-members/hooks/useCompanyMembers';

type TCompanyMemberFilterProps = {
  onFilter: (filterProps: Omit<ICompanyMemberRequest, 'parentUserId'>) => void;
};

export function CompanyMemberFilter({ onFilter }: TCompanyMemberFilterProps) {
  const { t } = useTranslation();
  const { setValue, getValues, watch, register, reset } = useFormContext();
  const { hasAllItems, statusOptions } = useCompanyMembers();

  const onFilterNameId = useCallback(() => {
    setValue('pageNumber', 0);
    const { status, ...filterProps } = getValues();
    onFilter({ ...filterProps, pageNumber: 1, userStatus: status?.[0] });
  }, [onFilter, setValue, getValues]);

  const onFilterStatus = useCallback(
    (status: string) => {
      setValue('status', [status]);
      if (!hasAllItems) {
        setValue('pageNumber', 0);
        const { status: userStatus, ...filterProps } = getValues();
        onFilter({ ...filterProps, pageNumber: 1, userStatus: status });
      }
    },
    [onFilter, hasAllItems, getValues, setValue],
  );

  const items: TFilterItem[] = [
    {
      id: 'filterNameId',
      label: t('pages.dashboard.users.filters.userName'),
      type: 'CUSTOM',
      dropdownContent: (
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-3">
            <span className="typo-subtitle-subtitle3 text-helper uppercase">
              {t('pages.dashboard.users.filters.searchById')}
            </span>
            <div className="flex items-center">
              <Input
                label=""
                type="text"
                {...register('userId')}
                size="s"
                icon={{ right: hasAllItems ? 'search' : undefined }}
              />
              {!hasAllItems && (
                <IconButton icon="search" variant="secondary" disabled={!watch('userId')} onClick={onFilterNameId} />
              )}
            </div>
          </div>
          <Divider />
          <div className="flex flex-col gap-3">
            <span className="typo-subtitle-subtitle3 text-helper uppercase">
              {t('pages.dashboard.users.filters.searchByName')}
            </span>
            <div className="flex items-center">
              <Input
                label=""
                type="text"
                size="s"
                icon={{ right: hasAllItems ? 'search' : undefined }}
                {...register('name')}
              />
              {!hasAllItems && (
                <IconButton icon="search" variant="secondary" disabled={!watch('name')} onClick={onFilterNameId} />
              )}
            </div>
          </div>
        </div>
      ),
      selectedValues: [watch('name'), watch('userId')].filter(Boolean),
      onReset: () => {
        setValue('userId', '');
        setValue('name', '');
        onFilter({ userStatus: watch('status')[0] });
      },
    },
    {
      id: 'filterUserStatus',
      label: t('pages.dashboard.users.filters.userStatus'),
      type: 'CUSTOM',
      dropdownContent: (
        <RadioList
          label=""
          options={statusOptions}
          value={watch('status')[0]}
          onChange={onFilterStatus}
          className="gap-4 p-5"
        />
      ),
      selectedValues: statusOptions
        ?.filter((option) => option.value === watch('status')?.[0])
        ?.map(({ label }) => label),
      onReset: () => {
        setValue('status', []);
        onFilter({ userId: watch('userId'), name: watch('name') });
      },
    },
  ];

  return (
    <Filters
      labels={{
        filterButton: t('pages.cores.filter'),
        selectedValues: '',
        clearButton: t('pages.cores.clearFilters'),
      }}
      items={items}
      defaultFilters={['filterNameId', 'filterUserStatus']}
      onClearFilters={() => {
        onFilter({});
        reset();
      }}
    />
  );
}
export default CompanyMemberFilter;
